import React from 'react';

import { MessageCircle, Share2 as Share, MoreHorizontal } from 'react-feather';
import { Dropdown, Button } from 'app/seedly-component-library';
import { useSelector } from 'react-redux';
import Reactions from 'app/components/post/Reactions';
import UserPassport from 'app/components/profile/UserPassport';
import BookmarkButton from 'app/components/post/BookmarkButton';
import EngagementBarLayout from 'app/components/post/EngagementBarLayout';
import CommentList from 'app/components/comment/CommentList';
import PostCommentForm from 'app/components/comment/PostCommentForm';
import { UPVOTER_TYPE, SAVE_TYPE } from 'app/constants/app';
import { generateOpinionLink } from 'app/utils/generateShareLink';
import { RootState } from 'app/reducer';
import { Opinion } from 'entity/opinion/opinionTypes';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import OpinionBody from './OpinionBody';
import useOpinionMenu from './hooks/useOpinionMenu';
import useShareMenu from '../../../utils/hooks/useShareMenu';

const OpinionItem = ({
  opinion,
  withComment = true,
}: {
  opinion: Opinion;
  withComment: boolean;
}) => {
  const {
    id,
    post,
    user,
    updatedAt,
    publishedAt,
    slug,
    commentsCount,
    parentCommentsCount,
    credential,
    createdByCurrentUser,
    pageviewsCount,
  } = opinion;

  const shareUrl = generateOpinionLink(slug);
  const menuOptions = useOpinionMenu({ opinion });
  const shareMenu = useShareMenu(shareUrl);
  const profile = useSelector((state: RootState) => state.profile.user);

  return (
    <div className="card" data-testid="opinion-item-card">
      <div className="flex flex-col w-full gap-4">
        <UserPassport
          profile={createdByCurrentUser ? profile : user}
          createdAt={publishedAt}
          updatedAt={updatedAt}
          avatarSize="avatar-12"
          credential={credential || user.defaultCredential}
        />
        <OpinionBody opinion={opinion} titleLink={`/opinions/${slug}`} />
        <EngagementBarLayout
          viewCount={pageviewsCount}
          commentsCount={commentsCount}
          commentButton={<Button icon={<MessageCircle />} disabled />}
          shareButton={
            <Dropdown
              items={shareMenu}
              handleSelectedItemchange={item => {
                item.selectedItem.onClick();
              }}
              menuAlign="left"
              button={
                <Button
                  variant="ghost"
                  data-testid="button-share-options"
                  onClick={() => {
                    mixpanel.track(trackedEvents.CommentThread, {
                      param: 'share',
                      category: 'opinion',
                    });
                  }}
                  icon={<Share />}
                />
              }
            />
          }
          bookmarkButton={
            <BookmarkButton
              isOwnContent={createdByCurrentUser}
              upvoted={post.upvoted}
              contentId={id}
              saved={post.bookmarked}
              onClickTrack={() => {
                mixpanel.track(trackedEvents.CommentThread, {
                  param: 'bookmark',
                  category: 'opinion',
                });
              }}
              type={SAVE_TYPE.OPINION}
            />
          }
          reactionButton={
            <Reactions
              id={id}
              type={UPVOTER_TYPE.OPINION}
              upvoteCount={post.upvotesCount}
              upvoted={post.upvoted}
            />
          }
          menuButton={
            menuOptions.length > 0 && (
              <Dropdown
                items={menuOptions}
                handleSelectedItemchange={item => {
                  item.selectedItem.onClick();
                }}
                menuAlign="right"
                button={
                  <Button
                    size="md"
                    variant="ghost"
                    data-testid="menu-options"
                    onClick={() => {
                      mixpanel.track(trackedEvents.CommentThread, {
                        param: 'three_dot_menu',
                        category: 'opinion',
                      });
                    }}
                    icon={<MoreHorizontal />}
                  />
                }
              />
            )
          }
        />
        {withComment && (
          <>
            <div className="horizontal-divider" />
            <PostCommentForm commentableType="Opinion" commentableId={id} />
            <CommentList
              totalCommentCount={parentCommentsCount}
              commentableId={id}
              commentableType="Opinion"
              childCommentableType="OpinionComment"
              commentShareLink={shareUrl}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OpinionItem;
